import { useEffect, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import InquiryForm from "../../components/InquiryForm/InquiryForm";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import styles from "./Inquiry.module.css";

/*
 * LOCAL DEVELOPMENT OVERRIDE
 *
 * This ONLY takes effect when running on localhost.
 *
 * ENABLE_LOCAL_INQUIRY_OVERRIDE:
 *   true  = use the local override
 *   false = use normal Firebase logic
 *
 * SHOW_FORM_LOCALLY:
 *   true  = force the inquiry form open
 *   false = force the "Bookings Are Currently Closed" state
 */
const ENABLE_LOCAL_INQUIRY_OVERRIDE = false;
const SHOW_FORM_LOCALLY = false;

const Inquiry = () => {
  const [isPaused, setIsPaused] = useState(false);
  const [resumeDate, setResumeDate] = useState("");
  const [resumeDateDisplay, setResumeDateDisplay] = useState("");
  const [loading, setLoading] = useState(true);

  const resumeCheckIntervalRef = useRef(null);

  const isLocalDevelopment =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  const isPauseCurrentlyActive = (paused, dateValue) => {
    if (!paused || !dateValue) return false;

    const resumeJsDate =
      typeof dateValue?.toDate === "function"
        ? dateValue.toDate()
        : new Date(dateValue);

    return new Date() < resumeJsDate;
  };

  useEffect(() => {
    const settingsRef = doc(db, "settings", "inquiryAvailability");

    const unsubscribe = onSnapshot(
      settingsRef,
      (snapshot) => {
        try {
          if (!snapshot.exists()) {
            setIsPaused(false);
            setResumeDate("");
            setResumeDateDisplay("");
            setLoading(false);
            return;
          }

          const { isPaused = false, resumeDate = null } = snapshot.data();

          const activePause = isPauseCurrentlyActive(isPaused, resumeDate);

          setIsPaused(activePause);

          if (resumeDate) {
            const resumeJsDate =
              typeof resumeDate?.toDate === "function"
                ? resumeDate.toDate()
                : new Date(resumeDate);

            const year = resumeJsDate.getFullYear();
            const month = String(resumeJsDate.getMonth() + 1).padStart(2, "0");
            const day = String(resumeJsDate.getDate()).padStart(2, "0");

            setResumeDate(`${year}-${month}-${day}`);

            setResumeDateDisplay(
              resumeJsDate.toLocaleDateString(undefined, {
                month: "long",
                day: "numeric",
                year: "numeric",
              }),
            );
          } else {
            setResumeDate("");
            setResumeDateDisplay("");
          }
        } catch (err) {
          console.error("Error processing inquiry availability:", err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error("Error subscribing to inquiry availability:", err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isPaused || !resumeDate) return;

    const checkResumeDate = () => {
      const resumeJsDate = new Date(resumeDate + "T00:00:00");

      if (new Date() >= resumeJsDate) {
        setIsPaused(false);
        setResumeDate("");
        setResumeDateDisplay("");
      }
    };

    checkResumeDate();

    resumeCheckIntervalRef.current = setInterval(checkResumeDate, 60000);

    return () => {
      if (resumeCheckIntervalRef.current) {
        clearInterval(resumeCheckIntervalRef.current);
      }
    };
  }, [isPaused, resumeDate]);

  /*
   * Firebase remains the source of truth everywhere by default.
   *
   * The override only changes what localhost renders.
   */
  const shouldShowForm =
    isLocalDevelopment && ENABLE_LOCAL_INQUIRY_OVERRIDE
      ? SHOW_FORM_LOCALLY
      : !isPaused;

  return (
    <section className={styles.container}>
      {loading ? (
        <div className={styles.loadingWrapper}>
          <LoadingSpinner />
          <p className={styles.loading}>Loading inquiry form...</p>
        </div>
      ) : shouldShowForm ? (
        <InquiryForm />
      ) : (
        <div className={styles.messageCard}>
          <h1 className={styles.title}>
            <span className={styles.titleSparkle} aria-hidden="true">
              ✨
            </span>
            <span className={styles.titleText}>
              Bookings Are Currently Closed
            </span>
            <span className={styles.titleSparkle} aria-hidden="true">
              ✨
            </span>
          </h1>
          <p className={styles.pauseText}>
            Marléna is not taking appointments right now.
          </p>
          {resumeDateDisplay && (
            <p className={styles.resumeDate}>
              Inquiries will reopen on {resumeDateDisplay}.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default Inquiry;